import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaBook, FaFlask, FaPalette, FaMusic, FaFutbol } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import './Services.css';

const Services = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const formRef = useRef();

  const services = [
    {
      icon: <FaGraduationCap />,
      title: "Academic Excellence",
      description: "Comprehensive curriculum designed for holistic development",
      image: "/assets/graphic.JPG",
      features: ["CBSE Curriculum", "Experienced Faculty", "Smart Classrooms", "Regular Assessments"]
    },
    {
      icon: <FaFlask />,
      title: "Science & Technology",
      description: "State-of-the-art labs and modern learning tools",
      image: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8c2NpZW5jZSUyMGxhYnxlbnwwfHwwfHx8&auto=format&fit=crop&w=500&q=60",
      features: ["Modern Labs", "Practical Learning", "Tech Integration", "Research Projects"]
    },
    {
      icon: <FaPalette />,
      title: "Arts & Creativity",
      description: "Nurturing creative expression and artistic talents",
      image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8YXJ0c3xlbnwwfHwwfHx8&auto=format&fit=crop&w=500&q=60",
      features: ["Visual Arts", "Performing Arts", "Exhibitions", "Cultural Events"]
    },
    {
      icon: <FaFutbol />,
      title: "Sports & Athletics",
      description: "Comprehensive sports facilities and training",
      image: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fHNwb3J0c3xlbnwwfHwwfHx8&auto=format&fit=crop&w=500&q=60",
      features: ["Sports Complex", "Professional Coaching", "Tournaments", "Fitness Programs"]
    },
    {
      icon: <FaMusic />,
      title: "Music & Performance",
      description: "Dedicated spaces for musical excellence",
      image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bXVzaWN8ZW58MHx8MHx8fA%3D%3D&auto=format&fit=crop&w=500&q=60",
      features: ["Music Room", "Instruments", "Live Shows", "Expert Training"]
    },
    {
      icon: <FaBook />,
      title: "Library & Resources",
      description: "Extensive collection of books and digital resources",
      image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8bGlicmFyeXxlbnwwfHwwfHx8&auto=format&fit=crop&w=500&q=60",
      features: ["Digital Library", "Study Areas", "Online Resources", "Research Support"]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await emailjs.sendForm(
        'service_r4rdqel',
        'template_2nemcjs',
        formRef.current,
        'r4h-QQHVf4PaVzNiV'
      );
      alert('Message sent successfully!');
      setShowContactForm(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="services-container">
      <motion.div 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1552674605-db6ffd4facb5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fHNwb3J0c3xlbnwwfHwwfHx8&auto=format&fit=crop&w=500&q=60)` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <motion.h1
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Academic Programs & Facilities
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Discover our world-class facilities and comprehensive educational programs
          </motion.p>
          <motion.button
            className="hero-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowContactForm(true)}
          >
            Enquire Now
          </motion.button>
        </div>
      </motion.div>

      <div className="services-grid">
        {services.map((service, index) => (
          <motion.div
            key={index}
            className="service-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 }}
          >
            <div className="service-image">
              <img src={service.image} alt={service.title} />
              <div className="image-overlay">
                <div className="service-icon">{service.icon}</div>
              </div>
            </div>
            <div className="service-content">
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="features-list">
                {service.features.map((feature, idx) => (
                  <li key={idx}>
                    <span className="feature-bullet">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <motion.button
                className="learn-more"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowContactForm(true)}
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {showContactForm && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="contact-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <form ref={formRef} onSubmit={handleSubmit}>
              <h2>Admission Inquiry</h2>
              <div className="form-group">
                <input type="text" name="studentName" placeholder="Student's Full Name*" required />
              </div>
              <div className="form-group">
                <input type="text" name="parentName" placeholder="Parent/Guardian Name*" required />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Email Address*" required />
              </div>
              <div className="form-group">
                <input type="tel" name="phone" placeholder="Phone Number*" required />
              </div>
              <div className="form-group">
                <select name="grade" required>
                  <option value="">Grade Applying For*</option>
                  <option value="1">Grade 1</option>
                  <option value="2">Grade 2</option>
                  {/* Add more grades */}
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder="Additional Message" rows="4"></textarea>
              </div>
              <button type="submit" className="submit-button">Submit Application</button>
            </form>
            <button className="close-modal" onClick={() => setShowContactForm(false)}>&times;</button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Services;